export const EVENT_STATE = Object.freeze({
  // `opt_in` event the player has not joined yet -- nothing is counted for
  // them until they do.
  NEEDS_OPT_IN: "needs_opt_in",
  // Below the win condition target.
  IN_PROGRESS: "in_progress",
  // At 100%, rewards not booked yet.
  COMPLETE: "complete",
  // Rewards already booked.
  CLAIMED: "claimed",
});

// The action buttons the modal offers, per `docs/event-system.md` 3.1-3.3.
export const EVENT_ACTION = Object.freeze({
  OPT_IN: "opt_in",
  GET_PREMIUM: "get_premium",
  // Free-only claim -- routes through the Claim Free Stumble screen first.
  CLAIM_FREE: "claim_free",
  // Free + premium in one call, shown once the player is premium.
  CLAIM_REWARDS: "claim_rewards",
});

// How premium can be bought for this event.
export const PREMIUM_MODE = Object.freeze({
  // `premium.cost` is non-zero -> POST /premium debits gold/crowns directly.
  CURRENCY: "currency",
  // `premium.pay_package_id` -> buy through payment/*; premium is granted
  // asynchronously, so the event has to be refetched afterwards.
  PAYMENT: "payment",
  // Neither configured -- premium cannot be bought at all.
  NONE: "none",
});

// Media fields come back as arrays of `{ url, name, mimetype, ... }`, but
// tolerate a bare object or string too.
export const resolveAssetUrl = (event, key) => {
  const media = event && event[key];
  if (Array.isArray(media)) return (media[0] && media[0].url) || "";
  if (media && typeof media === "object") return media.url || "";
  return typeof media === "string" ? media : "";
};

export const userStateOf = (event) => (event && event.user) || {};

export const isPremium = (event) => !!userStateOf(event).is_premium;

export const isOptedIn = (event) => !!userStateOf(event).opted_in;

// True only when the event demands an opt-in the player has not done.
// Events without `opt_in` count from the first matching action on their own.
export const needsOptIn = (event) =>
  !!(event && event.opt_in) && !isOptedIn(event);

export const isClaimed = (event) => !!userStateOf(event).claimed_at;

export const isPremiumClaimed = (event) =>
  !!userStateOf(event).premium_claimed_at;

export const progressOf = (event) => Number(userStateOf(event).progress) || 0;

export const targetOf = (event) => Number(event && event.target) || 0;

// Prefer the server's own percentage; fall back to progress/target. Clamped,
// because a premium boost can push absolute progress past the target.
export const progressPercent = (event) => {
  const given = userStateOf(event).progress_percent;
  const percent =
    given != null && given !== ""
      ? Number(given)
      : targetOf(event) > 0
      ? (progressOf(event) / targetOf(event)) * 100
      : 0;

  if (!Number.isFinite(percent)) return 0;
  return Math.min(100, Math.max(0, percent));
};

export const isComplete = (event) => progressPercent(event) >= 100;

// `can_claim` is authoritative when present -- the server knows about
// conditions the client cannot see.
export const canClaim = (event) => {
  const user = userStateOf(event);
  if (user.can_claim != null) return !!user.can_claim;
  return isComplete(event) && !isClaimed(event);
};

export const eventState = (event) => {
  if (needsOptIn(event)) return EVENT_STATE.NEEDS_OPT_IN;
  if (isClaimed(event)) return EVENT_STATE.CLAIMED;
  if (isComplete(event)) return EVENT_STATE.COMPLETE;
  return EVENT_STATE.IN_PROGRESS;
};

export const premiumMode = (event) => {
  const premium = (event && event.premium) || {};
  const cost = premium.cost || {};
  if (Number(cost.gold) > 0 || Number(cost.crowns) > 0) {
    return PREMIUM_MODE.CURRENCY;
  }
  if (premium.pay_package_id) return PREMIUM_MODE.PAYMENT;
  return PREMIUM_MODE.NONE;
};

export const canBuyPremium = (event) =>
  !isPremium(event) && premiumMode(event) !== PREMIUM_MODE.NONE;

// Which buttons the modal shows, in display order.
//
// One button slot only (wireframe 6.2):
//
//   in progress, free     -> Get Premium
//   in progress, premium  -> Claim Rewards        (upgraded state, 1.3 -- shown
//                                                  but not claimable until 100%)
//   complete,    free     -> Get Premium + Claim Free
//                                                 (Claim Free as the "or claim
//                                                  free reward" link below;
//                                                  the main button on its own
//                                                  when premium is not for sale)
//   complete,    premium  -> Claim Rewards        (books free + premium)
//   claimed,     free     -> Get Premium          (free reward booked,
//                                                  premium still for sale)
//   claimed,     premium  -> Claim Rewards        (bought after the free
//                                                  claim; premium part open,
//                                                  per the server's
//                                                  `can_claim`)
//                           or nothing            (everything booked)
export const availableActions = (event) => {
  const state = eventState(event);

  if (state === EVENT_STATE.NEEDS_OPT_IN) return [EVENT_ACTION.OPT_IN];
  if (state === EVENT_STATE.CLAIMED) {
    if (isPremium(event)) {
      return canClaim(event) ? [EVENT_ACTION.CLAIM_REWARDS] : [];
    }
    return canBuyPremium(event) ? [EVENT_ACTION.GET_PREMIUM] : [];
  }

  // Upgraded: Claim Rewards takes Get Premium's slot for the whole event.
  if (isPremium(event)) return [EVENT_ACTION.CLAIM_REWARDS];

  if (state === EVENT_STATE.COMPLETE) {
    return canBuyPremium(event)
      ? [EVENT_ACTION.GET_PREMIUM, EVENT_ACTION.CLAIM_FREE]
      : [EVENT_ACTION.CLAIM_FREE];
  }
  return canBuyPremium(event) ? [EVENT_ACTION.GET_PREMIUM] : [];
};

// Countdown target as epoch ms, matching how the offer chain badge does it.
// Prefers the absolute `end_at`; falls back to `seconds_remaining`.
// Returns null for an open-ended or expired event, so the timer stays hidden.
export const timerTarget = (event) => {
  if (!event) return null;

  let then;
  if (event.end_at) {
    then = new Date(event.end_at).getTime();
  } else if (event.seconds_remaining != null) {
    then = Date.now() + Number(event.seconds_remaining) * 1000;
  }

  if (!then || !Number.isFinite(then)) return null;
  return then - Date.now() > 0 ? then : null;
};

// Red bubble on the badge: a new event, cleared once the player opens it.
export const showBadgeBubble = (event) => !!userStateOf(event).is_new;

// Red bubble on the claim button: reward sitting there unclaimed -- also the
// premium part left after a free-only claim.
export const showClaimBubble = (event) => canClaim(event);

export const rewardsOf = (event, tier) => {
  const rewards = (event && event.rewards) || {};
  return rewards[tier] || [];
};

export const freeRewards = (event) => rewardsOf(event, "free");

export const premiumRewards = (event) => rewardsOf(event, "premium");

// `display_config` is documented as an object but may arrive as a JSON string,
// same as the season pass one.
export const displayConfig = (event) => {
  const raw = event && event.display_config;
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch (e) {
      return {};
    }
  }
  return raw;
};

// Localized `display_config.<section>.<key>` (`{ de, en }`), or "".
export const configText = (event, section, key, locale) => {
  const text = (displayConfig(event)[section] || {})[key];
  if (!text) return "";
  if (typeof text === "string") return text;
  return text[locale === "en" ? "en" : "de"] || "";
};

export const escapeHtml = (text) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Event `type` -> translation key of its task title; the flavour is
// `<key>.flavour`. `{0}` is the event's target.
const TASK_KEYS = {
  slot_spin: "event.task.slotspintasktype",
  total_slot_win: "event.task.totalslotwintasktype",
  instant_slot_win: "event.task.instantslotwintasktype",
  total_win_count: "event.task.totalwincounttasktype",
  level_up: "event.task.leveluptasktype",
  tournament_participation: "event.task.tournamentparticipationtasktype",
  tournament_win: "event.task.tournamentwintasktype",
  payment: "event.task.paymenttasktype",
  champion: "event.task.championtasktype",
};

export const taskKey = (event) => (event && TASK_KEYS[event.type]) || null;

// ACP texts are plain text with `\r\n` line breaks and `*word*` for
// emphasis. Escaped first, so the result is safe for v-html; line breaks are
// left to `white-space: pre-line`.
export const formatDescription = (text) => {
  if (!text) return "";
  return escapeHtml(String(text).replace(/\r\n?/g, "\n")).replace(
    /\*([^*\n]+)\*/g,
    "<b>$1</b>"
  );
};
